import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Tooltip,
  IconButton
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ======================================================
   💰 HELPERS MONEDA
====================================================== */
const formatCurrency = (value) => {
  if (!value) return "";
  const number = Number(value.replace(/\D/g, ""));
  if (!number) return "";
  return "$ " + number.toLocaleString("es-CO");
};

const parseCurrency = (value) => {
  return value.replace(/\D/g, "");
};

/* ======================================================
   🧩 CAMPO REUTILIZABLE
====================================================== */
const CampoConInfo = React.memo(
  ({ label, info, name, value, error, onChange, onBlur }) => {
    return (
      <Grid item xs={12}>
        <Box display="flex" alignItems="flex-start">
          <Box flexGrow={1}>
            <TextField
              fullWidth
              type="text"
              inputMode="numeric"
              label={label}
              value={formatCurrency(value)}
              onChange={(e) => {
                const input = e.target;
                const start = input.selectionStart;

                const raw = parseCurrency(input.value);
                onChange(name, raw);

                // 🔥 mantener cursor estable
                requestAnimationFrame(() => {
                  input.setSelectionRange(start, start);
                });
              }}
              onBlur={() => onBlur(name)}
              onKeyDown={(e) => {
                if (
                  !/[0-9]|Backspace|Tab|ArrowLeft|ArrowRight|Delete/.test(
                    e.key
                  )
                ) {
                  e.preventDefault();
                }
              }}
              error={Boolean(error)}
              helperText={error}
            />
          </Box>

          <Tooltip
            title={info}
            placement="left"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#333",
                  color: "#fff",
                  padding: "10px 14px",
                  fontSize: "0.9rem",
                  borderRadius: "8px",
                  maxWidth: 260
                }
              }
            }}
          >
            <IconButton sx={{ ml: 1, mt: "6px" }}>
              <InfoOutlinedIcon sx={{ color: "#666" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
    );
  }
);

/* ======================================================
   🧾 COMPONENTE PRINCIPAL
====================================================== */
export default function PasoAhorros({ data, update, next, back }) {
  const minAhorroObligatorio =
    data.salario && Number(data.salario) > 0
      ? Math.ceil(Number(data.salario) * 0.01)
      : 17500;

  const MIN_OTROS = 10000;

  const [form, setForm] = useState({
    ahorroObligatorio:
      data.ahorroObligatorio || String(minAhorroObligatorio),
    aLaVista: data.aLaVista || "",
    navideno: data.navideno || "",
    vehiculo: data.vehiculo || "",
    vivienda: data.vivienda || ""
  });

  const [errors, setErrors] = useState({});

  /* ================== HANDLERS ================== */

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (name) => {
    let clean = (form[name] || "").replace(/\D/g, "");
    let val = Number(clean || 0);

    let newValue = clean;
    let newError = "";

    if (name === "ahorroObligatorio") {
      if (val < minAhorroObligatorio) {
        newValue = String(minAhorroObligatorio);
        newError = `El valor mínimo debe ser de $${minAhorroObligatorio.toLocaleString(
          "es-CO"
        )}`;
      }
    } else {
      if (val > 0 && val < MIN_OTROS) {
        newValue = String(MIN_OTROS);
        newError = "El valor mínimo debe ser de $10.000";
      }
    }

    setForm((prev) => ({
      ...prev,
      [name]: newValue
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: newError
    }));
  };

  const continuar = () => {
    const newErrors = {};

    const ahorro = Number(form.ahorroObligatorio || 0);

    if (ahorro < minAhorroObligatorio) {
      newErrors.ahorroObligatorio = `El valor mínimo debe ser de $${minAhorroObligatorio.toLocaleString(
        "es-CO"
      )}`;
    }

    ["aLaVista", "navideno", "vehiculo", "vivienda"].forEach((k) => {
      const val = Number(form[k] || 0);
      if (val > 0 && val < MIN_OTROS) {
        newErrors[k] = "El valor mínimo debe ser de $10.000";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    update(form);
    next();
  };

  /* ================== UI ================== */

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={3}>
        Ahorros
      </Typography>

      <Grid container spacing={2}>
        <CampoConInfo
          name="ahorroObligatorio"
          label="Su ahorro (Obligatorio)"
          value={form.ahorroObligatorio}
          error={errors.ahorroObligatorio}
          onChange={handleChange}
          onBlur={handleBlur}
          info={`Es un ahorro mínimo del 1% sobre su salario básico: $${minAhorroObligatorio.toLocaleString("es-CO")}, este monto se acumula durante su vinculación laboral y se devuelve íntegramente al finalizar su contrato.
          `}
        />

        <CampoConInfo
          name="aLaVista"
          label="Ahorro a la vista (Opcional)"
          value={form.aLaVista}
          error={errors.aLaVista}
          onChange={handleChange}
          onBlur={handleBlur}
          info="Un ahorro opcional y flexible que le brinda disponibilidad inmediata de sus recursos en cualquier momento que lo necesite."
        />

        <CampoConInfo
          name="navideno"
          label="Ahorro navideño (Opcional)"
          value={form.navideno}
          error={errors.navideno}
          onChange={handleChange}
          onBlur={handleBlur}
          info="Un ahorro opcional pensado para sus gastos de fin de año. Se entrega el 30 de noviembre y genera un atractivo interés efectivo anual del 6%."
        />

        <CampoConInfo
          name="vehiculo"
          label="Ahorro de vehículo (Opcional)"
          value={form.vehiculo}
          error={errors.vehiculo}
          onChange={handleChange}
          onBlur={handleBlur}
          info="Un ahorro opcional diseñado para ayudarle a cumplir el sueño de tener vehículo propio. Le permite acceder a nuestra línea de crédito especial para compra de vehículo a través del Fondo de Empleados de ManpowerGroup."
        />

        <CampoConInfo
          name="vivienda"
          label="Ahorro de vivienda (Opcional)"
          value={form.vivienda}
          error={errors.vivienda}
          onChange={handleChange}
          onBlur={handleBlur}
          info="Ahorro opcional que te ayuda a cumplir el sueño de tener vivienda propia y te brinda acceso a una línea de crédito especial para compra de vivienda con beneficios exclusivos del Fondo de Empleados de ManpowerGroup."
        />
      </Grid>

      {/* BOTONES */}
      <Box display="flex" justifyContent="center" gap={3} mt={4}>
        <Button
          variant="outlined"
          size="large"
          onClick={back}
          sx={{
            borderRadius: "20px",
            px: 4,
            textTransform: "none"
          }}
        >
          Atrás
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={continuar}
          sx={{
            borderRadius: "20px",
            px: 4,
            textTransform: "none"
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
}